import { useState } from "react";
import { Twitter, Facebook, Linkedin, Link2, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { logger } from "../lib/logger";

const Btn = ({ onClick, href, label, testid, children }) => {
  const Comp = href ? "a" : "button";
  return (
    <Comp
      type={href ? undefined : "button"}
      href={href}
      target={href ? "_blank" : undefined}
      rel={href ? "noopener noreferrer" : undefined}
      onClick={onClick}
      data-testid={testid}
      aria-label={label}
      title={label}
      className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--border)] text-[color:var(--text-muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors"
    >
      {children}
    </Comp>
  );
};

export const SocialShare = ({ url, title, description = "" }) => {
  const [copied, setCopied] = useState(false);
  const enc = encodeURIComponent;

  const shareX = `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`;
  const shareFb = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
  const shareLi = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
  const shareWa = `https://api.whatsapp.com/send?text=${enc(`${title} — ${url}`)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      logger.warn("[social-share] clipboard write failed:", err);
      toast.error("Impossible de copier le lien");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description, url });
      } catch (err) {
        // User cancelled or share API rejected — informational only
        if (err?.name !== "AbortError") logger.warn("[social-share] native share failed:", err);
      }
    } else {
      copy();
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="social-share">
      <span className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)] mr-1">Partager</span>
      <Btn href={shareX} label="Partager sur X (Twitter)" testid="share-x">
        <Twitter className="w-4 h-4" />
      </Btn>
      <Btn href={shareFb} label="Partager sur Facebook" testid="share-fb">
        <Facebook className="w-4 h-4" />
      </Btn>
      <Btn href={shareLi} label="Partager sur LinkedIn" testid="share-li">
        <Linkedin className="w-4 h-4" />
      </Btn>
      <Btn href={shareWa} label="Partager sur WhatsApp" testid="share-wa">
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
          <path d="M17.6 6.3A7.8 7.8 0 0 0 12 4a7.86 7.86 0 0 0-6.67 11.92L4 20l4.22-1.3A7.9 7.9 0 0 0 12 19.8a7.86 7.86 0 0 0 5.6-13.5ZM12 18.4a6.54 6.54 0 0 1-3.33-.92l-.24-.14-2.5.77.77-2.43-.15-.25A6.56 6.56 0 1 1 18.56 12 6.55 6.55 0 0 1 12 18.4Zm3.6-4.9c-.2-.1-1.17-.58-1.35-.65s-.31-.1-.44.1-.5.65-.62.78-.23.15-.43.05a5.4 5.4 0 0 1-1.58-.98 6 6 0 0 1-1.1-1.37c-.12-.2 0-.3.09-.41s.2-.23.3-.35a1.4 1.4 0 0 0 .2-.33.37.37 0 0 0 0-.35c0-.1-.44-1.06-.6-1.45s-.32-.33-.44-.33h-.38a.72.72 0 0 0-.53.25 2.23 2.23 0 0 0-.7 1.66 3.87 3.87 0 0 0 .82 2.06 8.93 8.93 0 0 0 3.42 3.02c.48.2.86.33 1.15.43a2.77 2.77 0 0 0 1.27.08 2.07 2.07 0 0 0 1.36-.96 1.69 1.69 0 0 0 .12-.96c-.04-.1-.18-.15-.38-.25Z" />
        </svg>
      </Btn>
      <Btn onClick={copy} label="Copier le lien" testid="share-copy">
        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Link2 className="w-4 h-4" />}
      </Btn>
      {typeof navigator !== "undefined" && typeof navigator.share === "function" && (
        <Btn onClick={nativeShare} label="Partager..." testid="share-native">
          <Share2 className="w-4 h-4" />
        </Btn>
      )}
    </div>
  );
};

export default SocialShare;
